import type { ComponentProps, ReactNode } from 'react'
import { Link } from '@/i18n/navigation'
import { resolveHref } from '@/lib/href'
import { cn } from '@/lib/cn'
import type { Cta } from '@/types/content'
import { Icon } from './Icon'

type Variant = 'primary' | 'secondary' | 'ghost' | 'onBrand' | 'ghostOnBrand'
type Size = 'md' | 'lg'

const BASE =
  'inline-flex items-center justify-center gap-2 rounded-full font-semibold whitespace-nowrap ' +
  'transition-[background-color,color,box-shadow,transform] duration-200 ease-[var(--ease-out-expo)] ' +
  'focus-visible:outline-3 focus-visible:outline-offset-2 focus-visible:outline-primary-500 ' +
  'disabled:pointer-events-none disabled:opacity-60 motion-safe:active:translate-y-px'

const VARIANTS: Record<Variant, string> = {
  primary:
    'bg-primary-500 text-white shadow-sm hover:bg-primary-600 hover:shadow-md focus-visible:outline-primary-700',
  secondary:
    'bg-white text-ink-800 ring-1 ring-border hover:ring-primary-300 hover:text-primary-700 hover:shadow-sm',
  ghost: 'text-primary-700 hover:bg-primary-50',
  onBrand: 'bg-white text-primary-700 shadow-sm hover:bg-primary-50 focus-visible:outline-white',
  ghostOnBrand:
    'text-white ring-1 ring-white/40 hover:bg-white/10 hover:ring-white/70 focus-visible:outline-white',
}

const SIZES: Record<Size, string> = {
  md: 'h-11 px-5 text-[0.9375rem]',
  lg: 'h-13 px-7 text-base',
}

interface ButtonContentProps {
  label: ReactNode
  icon?: string
  iconPosition?: 'start' | 'end'
}

function ButtonContent({ label, icon, iconPosition = 'end' }: ButtonContentProps) {
  const glyph = icon ? <Icon name={icon} className="size-[1.125em] shrink-0" /> : null
  return (
    <>
      {iconPosition === 'start' ? glyph : null}
      <span>{label}</span>
      {iconPosition === 'end' ? glyph : null}
    </>
  )
}

interface LinkButtonProps extends ButtonContentProps {
  cta: Cta
  variant?: Variant
  size?: Size
  className?: string
}

/** Bouton-lien : la destination et le libellé viennent TOUJOURS du contenu. */
export function LinkButton({
  cta,
  variant = 'primary',
  size = 'md',
  className,
  icon,
  iconPosition,
  label,
}: LinkButtonProps) {
  const target = resolveHref(cta)
  const classes = cn(BASE, VARIANTS[variant], SIZES[size], className)

  if (target.external) {
    return (
      <a href={target.href} className={classes} rel="noreferrer noopener" target="_blank">
        <ButtonContent label={label} icon={icon} iconPosition={iconPosition} />
      </a>
    )
  }

  return (
    <Link href={target.href as ComponentProps<typeof Link>['href']} className={classes}>
      <ButtonContent label={label} icon={icon} iconPosition={iconPosition} />
    </Link>
  )
}

interface ButtonProps extends ComponentProps<'button'>, ButtonContentProps {
  variant?: Variant
  size?: Size
}

export function Button({
  variant = 'primary',
  size = 'md',
  className,
  label,
  icon,
  iconPosition,
  ...props
}: ButtonProps) {
  return (
    <button className={cn(BASE, VARIANTS[variant], SIZES[size], className)} {...props}>
      <ButtonContent label={label} icon={icon} iconPosition={iconPosition} />
    </button>
  )
}
